import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useAuth } from "../../context/AuthContext";
import { useToast } from "../../hooks/use-toast";
import { calculateVATInclusiveAmount, getPaymentStructureLabel } from "../../lib/appUtils";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Card, CardContent } from "../ui/card";
import RichBodyEditor from "../admin/RichBodyEditor";
import { Badge } from "../ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Textarea } from "../ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction } from "../ui/alert-dialog";
import { Checkbox } from "../ui/checkbox";
import {
  Plus, Upload, FileText, CheckCircle, XCircle, CreditCard,
  Calendar, Copy, Send, ArrowRight, AlertTriangle, Crown, Lock, Eye, Shield, PiggyBank,
  Bookmark, Trash2, Sparkles
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const CreateProjectModal = ({ isOpen, onClose, preselectedStructure, onSuccess }) => {
  const [formData, setFormData] = useState({
    title: '',
    client_name: '',
    client_email: '',
    client_company: '',
    client_billing_address: '',
    client_billing_city: '',
    client_billing_postcode: '',
    client_billing_country: 'United Kingdom',
    total_amount: '',
    invoice_due_days: 14,
    is_invoice_project: false,
    invoice_type: 'generated',  // 'generated', 'uploaded', 'accounting'
    accounting_software: '',
    accounting_invoice_id: '',
    external_invoice_reference: '',
    custom_scope: '',
    payment_structure: null, // For client choice projects
    vat_rate: 20,
    vat_enabled: false, // Default to VAT disabled
    payment_protection_enabled: null  // Inherit from global setting
  });
  const [uploadedFile, setUploadedFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sendingToClient, setSendingToClient] = useState(false);
  const [showSendConfirmation, setShowSendConfirmation] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  const [saveAsTemplate, setSaveAsTemplate] = useState(false);
  const [templateName, setTemplateName] = useState('');
  const { token, user } = useAuth();
  const { toast } = useToast();

  // Check if user has Paify Plus or Embedded subscription
  const hasPlusSubscription = user?.subscription_plan === 'paidd_plus' || user?.subscription_plan === 'embedded';

  useEffect(() => {
    setFormData(prev => ({
      ...prev,
      payment_structure: preselectedStructure || null // Convert empty string to null
    }));
  }, [preselectedStructure]);

  // Fetch user's saved templates when modal opens
  const fetchTemplates = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/project-templates`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTemplates(res.data || []);
    } catch (err) {
      // Silent fail — templates are optional
      console.error('Failed to fetch templates:', err);
    }
  }, [token]);

  useEffect(() => {
    if (isOpen) {
      fetchTemplates();
      setSelectedTemplateId('');
      setSaveAsTemplate(false);
      setTemplateName('');
    }
  }, [isOpen, fetchTemplates]);

  const applyTemplate = (templateId) => {
    setSelectedTemplateId(templateId);
    if (!templateId) return;
    const t = templates.find(x => x.id === templateId);
    if (!t) return;
    setFormData(prev => ({
      ...prev,
      title: t.title || prev.title,
      client_name: t.client_name || '',
      client_email: t.client_email || '',
      client_company: t.client_company || '',
      client_billing_address: t.client_billing_address || '',
      client_billing_city: t.client_billing_city || '',
      client_billing_postcode: t.client_billing_postcode || '',
      client_billing_country: t.client_billing_country || 'United Kingdom',
      total_amount: t.total_amount ? String(t.total_amount) : prev.total_amount,
      invoice_due_days: t.invoice_due_days || 14,
      custom_scope: t.custom_scope || '',
      vat_enabled: !!t.vat_enabled,
      vat_rate: t.vat_rate ?? 20,
    }));
    toast({
      title: 'Template applied',
      description: `Loaded "${t.template_name}". Review and adjust before creating.`
    });
  };

  const deleteTemplate = async (templateId, templateLabel) => {
    try {
      await axios.delete(`${API}/project-templates/${templateId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTemplates(prev => prev.filter(t => t.id !== templateId));
      if (selectedTemplateId === templateId) setSelectedTemplateId('');
      toast({ title: 'Template deleted', description: `Removed "${templateLabel}".` });
    } catch (err) {
      toast({
        title: 'Could not delete',
        description: err.response?.data?.detail || 'Please try again.',
        variant: 'destructive'
      });
    }
  };

  const persistTemplateIfRequested = async () => {
    if (!saveAsTemplate) return;
    const cleanName = templateName.trim();
    if (!cleanName) return;
    try {
      const res = await axios.post(`${API}/project-templates`, {
        template_name: cleanName,
        title: formData.title,
        client_name: formData.client_name,
        client_email: formData.client_email,
        client_company: formData.client_company,
        client_billing_address: formData.client_billing_address,
        client_billing_city: formData.client_billing_city,
        client_billing_postcode: formData.client_billing_postcode,
        client_billing_country: formData.client_billing_country,
        total_amount: parseFloat(formData.total_amount) || 0,
        invoice_due_days: formData.invoice_due_days || 14,
        custom_scope: formData.custom_scope,
        vat_enabled: formData.vat_enabled,
        vat_rate: formData.vat_rate,
      }, { headers: { Authorization: `Bearer ${token}` } });
      setTemplates(prev => [res.data, ...prev]);
      toast({ title: 'Template saved', description: `"${cleanName}" ready for reuse.` });
    } catch (err) {
      toast({
        title: 'Template not saved',
        description: err.response?.data?.detail || 'Project was still created.',
        variant: 'destructive'
      });
    }
  };

  const handleSubmit = async (e, sendToClient = false) => {
    e.preventDefault();
    
    setLoading(true);
    if (sendToClient) setSendingToClient(true);

    try {
      const payload = {
        ...formData,
        currency: 'GBP', // Always use GBP
        total_amount: parseFloat(formData.total_amount),
        is_invoice_project: formData.is_invoice_project, // Use the toggle value directly
        payment_structure: formData.is_invoice_project ? 'invoice' : 'fifty_fifty', // Temporary default - client will choose actual payment method
        send_to_client: false // Don't use backend email - we'll use user's SMTP instead
      };

      console.log('Creating project with payload:', payload);
      console.log('VAT enabled in payload:', payload.vat_enabled);
      console.log('Full formData before sending:', formData);

      const response = await axios.post(`${API}/projects`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const createdProject = response.data;
      
      // If sendToClient is true, send email using user's SMTP settings
      if (sendToClient && createdProject.id) {
        const emailData = {
          to: formData.client_email,
          subject: `Payment Link for ${formData.title}`,
          template: 'payment_link',
          project_id: createdProject.id,
          payment_url: createdProject.hosted_url,
          email_type: 'payment_link'
        };
        
        try {
          await axios.post(`${API}/send-email`, emailData, {
            headers: { 
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          });
          
          toast({
            title: "Project Created & Sent!",
            description: `${formData.client_name} will receive an email at ${formData.client_email} with the payment link.`
          });
        } catch (emailError) {
          // Project was created but email failed
          toast({
            title: "Project Created",
            description: `Project saved but email failed: ${emailError.response?.data?.detail || 'Please check your email settings in Settings page.'}`,
            variant: "destructive"
          });
        }
      } else if (formData.is_invoice_project) {
        toast({
          title: "Invoice Project Created",
          description: "Invoice project has been created successfully. Look for the blue edit button (pencil icon) in your project list to open and edit invoice details."
        });
      } else {
        toast({
          title: "Project created",
          description: "Project saved. You can send it to your client when ready."
        });
      }
      
      setShowSendConfirmation(false);
      await persistTemplateIfRequested();
      onSuccess(createdProject);
    } catch (error) {
      console.error('Project creation error:', error);
      
      // Handle Pydantic validation errors from backend
      let errorMessage = "Failed to create project";
      
      if (error.response?.data?.detail) {
        if (Array.isArray(error.response.data.detail)) {
          // Pydantic validation errors - transform to user-friendly messages
          const validationErrors = error.response.data.detail.map(err => {
            const field = err.loc ? err.loc[err.loc.length - 1] : 'field';
            return `${field}: ${err.msg}`;
          });
          errorMessage = validationErrors.join(', ');
        } else if (typeof error.response.data.detail === 'string') {
          // Simple string error
          errorMessage = error.response.data.detail;
        }
      }
      
      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive"
      });
    } finally {
      setLoading(false);
      setSendingToClient(false);
    }
  };

  if (!isOpen) return null;

  return (
    <>
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent
        className="max-w-4xl max-h-[90vh] overflow-y-auto bg-gradient-to-br from-white to-gray-50"
        onPointerDownOutside={(e) => e.preventDefault()}
        onInteractOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
      >
        <DialogHeader className="pb-6 border-b border-gray-200">
          <DialogTitle className="font-heading text-2xl text-gray-900 flex items-center gap-2">
            <div className="w-8 h-8 bg-blue-500 rounded-lg flex items-center justify-center">
              <span className="text-white font-bold">+</span>
            </div>
            Create New Project
          </DialogTitle>
          <p className="text-gray-600 font-body mt-2">Set up a new project and send it to your client for approval</p>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-8 pt-6" autoComplete="off">
          {/* Template Picker — only shows when user has saved templates */}
          {templates.length > 0 && (
            <div data-testid="template-picker-panel" className="bg-gradient-to-r from-[#fff0f7] to-[#fef5ff] border border-pink-100 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 bg-[#e6007e] rounded-lg flex items-center justify-center flex-shrink-0">
                  <Sparkles className="w-4 h-4 text-white" />
                </div>
                <div className="flex-1 min-w-0">
                  <Label className="font-heading text-sm font-semibold text-gray-900 block">Apply a saved template</Label>
                  <p className="text-xs text-gray-600 font-body mb-2">One-click load a previous project setup. Review before submitting.</p>
                  <div className="flex flex-col sm:flex-row gap-2">
                    <Select value={selectedTemplateId} onValueChange={applyTemplate}>
                      <SelectTrigger data-testid="apply-template-select" className="font-body bg-white">
                        <SelectValue placeholder="Choose a template…" />
                      </SelectTrigger>
                      <SelectContent>
                        {templates.map((t) => (
                          <SelectItem key={t.id} value={t.id}>
                            {t.template_name}
                            {t.client_name ? ` — ${t.client_name}` : ''}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {selectedTemplateId && (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        data-testid="delete-template-btn"
                        className="font-body border-red-200 text-red-600 hover:bg-red-50 hover:text-red-700"
                        onClick={() => {
                          const t = templates.find(x => x.id === selectedTemplateId);
                          if (t && window.confirm(`Delete template "${t.template_name}"?`)) {
                            deleteTemplate(t.id, t.template_name);
                          }
                        }}
                      >
                        <Trash2 className="w-3.5 h-3.5 mr-1.5" />
                        Delete
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Project Information Section */}
          <div className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm">
            <h3 className="font-heading text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <span className="w-6 h-6 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center text-sm font-bold">1</span>
              Project Information
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="title" className="font-body text-gray-700 font-medium">Project Title</Label>
                <Input
                  id="title"
                  name="project_title_no_autofill"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="e.g. Website Redesign, Marketing Campaign"
                  className="font-body mt-1"
                  autoComplete="off"
                  autoCorrect="off"
                  autoCapitalize="off"
                  spellCheck="false"
                  data-form-type="other"
                  data-lpignore="true"
                  data-1p-ignore="true"
                  required
                />
              </div>

              <div>
                <Label htmlFor="total_amount" className="font-body text-gray-700 font-medium">Total Amount (excl. VAT)</Label>
                <div className="relative mt-1">
                  <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 font-bold">£</span>
                  <Input
                    id="total_amount"
                    name="amount_no_autofill"
                    type="number"
                    value={formData.total_amount}
                    onChange={(e) => setFormData({ ...formData, total_amount: e.target.value })}
                    placeholder="5000"
                    className="font-body pl-8"
                    autoComplete="off"
                    autoCorrect="off"
                    spellCheck="false"
                    data-form-type="other"
                    data-lpignore="true"
                    data-1p-ignore="true"
                    required
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Client Information Section */}
          <div className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm">
            <h3 className="font-heading text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <span className="w-6 h-6 bg-green-100 text-green-600 rounded-full flex items-center justify-center text-sm font-bold">2</span>
              Client Details
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              
              {/* Client name field */}
              <div>
                <Label htmlFor="client_name" className="font-body text-gray-700 font-medium">Client Name</Label>
                <Input
                  id="client_name"
                  name="client_name_no_autofill"
                  value={formData.client_name}
                  onChange={(e) => setFormData({ ...formData, client_name: e.target.value })}
                  placeholder="John Smith"
                  className="font-body mt-1"
                  autoComplete="off"
                  autoCorrect="off"
                  autoCapitalize="off"
                  spellCheck="false"
                  data-form-type="other"
                  data-lpignore="true"
                  data-1p-ignore="true"
                  required
                />
              </div>

              {/* Client email field */}
              <div>
                <Label htmlFor="client_email" className="font-body text-gray-700 font-medium">Client Email</Label>
                <Input
                  id="client_email"
                  name="client_email_no_autofill"
                  type="email"
                  value={formData.client_email}
                  onChange={(e) => setFormData({ ...formData, client_email: e.target.value })}
                  placeholder="john@company.com"
                  className="font-body mt-1"
                  autoComplete="off"
                  autoCorrect="off"
                  autoCapitalize="off"
                  spellCheck="false"
                  data-form-type="other"
                  data-lpignore="true"
                  data-1p-ignore="true"
                  required
                />
              </div>
            </div>
          </div>

          {/* Project Description Section */}
          <div className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm">
            <h3 className="font-heading text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <span className="w-6 h-6 bg-purple-100 text-purple-600 rounded-full flex items-center justify-center text-sm font-bold">3</span>
              Project Scope
            </h3>
            
            <div className="md:col-span-2">
              <Label htmlFor="custom_scope" className="font-body">Project Description &amp; Deliverables</Label>
              <div data-testid="project-scope-editor" className="mt-1">
                <RichBodyEditor
                  value={formData.custom_scope}
                  onChange={(html) => setFormData({ ...formData, custom_scope: html })}
                  placeholder={"Describe the project scope, deliverables, and key requirements. This will be included in the contract for legal clarity.\n\nExample:\n• Complete website redesign including homepage, about page, and contact page\n• Mobile-responsive design compatible with all modern browsers\n• SEO optimization and performance improvements\n• Training session for content management system\n• 2 rounds of revisions included"}
                  minHeight={180}
                />
              </div>
              <p className="text-xs text-gray-500 mt-1 font-body">
                This scope will be included in the contract to ensure legal clarity and protect both parties.
              </p>
            </div>

              <div>
                <Label htmlFor="client_company" className="font-body">Client Company (Optional)</Label>
                <Input
                  id="client_company"
                  name="client_company_no_autofill"
                  value={formData.client_company}
                  onChange={(e) => setFormData({ ...formData, client_company: e.target.value })}
                  placeholder="Company Ltd"
                  className="font-body"
                  autoComplete="off"
                  autoCorrect="off"
                  autoCapitalize="off"
                  spellCheck="false"
                  data-form-type="other"
                  data-lpignore="true"
                  data-1p-ignore="true"
                />
              </div>

            <div>
              <Label htmlFor="invoice_due_days" className="font-body">Invoice Due Days (for invoice option)</Label>
              <Input
                id="invoice_due_days"
                name="invoice_due_days_no_autofill"
                type="number"
                min="1"
                max="90"
                value={formData.invoice_due_days}
                onChange={(e) => setFormData({ ...formData, invoice_due_days: parseInt(e.target.value) })}
                className="font-body"
                autoComplete="off"
                data-form-type="other"
              />
              <p className="text-xs text-gray-500 mt-1 font-body">
                Default payment terms if client chooses invoice option
              </p>
            </div>
          </div>

          {/* Billing Address Section (Required for Contract) */}
          <div className="border border-gray-200 rounded-lg p-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 font-heading">Client Address (Required for Contract)</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <Label htmlFor="client_billing_address" className="font-body">Address</Label>
                <Input
                  id="client_billing_address"
                  name="billing_address_no_autofill"
                  value={formData.client_billing_address}
                  onChange={(e) => setFormData({ ...formData, client_billing_address: e.target.value })}
                  placeholder="123 High Street"
                  className="font-body"
                  autoComplete="off"
                  autoCorrect="off"
                  autoCapitalize="off"
                  spellCheck="false"
                  data-form-type="other"
                  data-lpignore="true"
                  data-1p-ignore="true"
                  required
                />
              </div>
              
              <div>
                <Label htmlFor="client_billing_city" className="font-body">City</Label>
                <Input
                  id="client_billing_city"
                  name="billing_city_no_autofill"
                  value={formData.client_billing_city}
                  onChange={(e) => setFormData({ ...formData, client_billing_city: e.target.value })}
                  placeholder="London"
                  className="font-body"
                  autoComplete="off"
                  autoCorrect="off"
                  autoCapitalize="off"
                  spellCheck="false"
                  data-form-type="other"
                  data-lpignore="true"
                  data-1p-ignore="true"
                  required
                />
              </div>
              
              <div>
                <Label htmlFor="client_billing_postcode" className="font-body">Postcode</Label>
                <Input
                  id="client_billing_postcode"
                  name="billing_postcode_no_autofill"
                  value={formData.client_billing_postcode}
                  onChange={(e) => setFormData({ ...formData, client_billing_postcode: e.target.value })}
                  placeholder="SW1A 1AA"
                  className="font-body"
                  autoComplete="off"
                  autoCorrect="off"
                  autoCapitalize="off"
                  spellCheck="false"
                  data-form-type="other"
                  data-lpignore="true"
                  data-1p-ignore="true"
                  required
                />
              </div>
              
              <div className="md:col-span-2">
                <Label htmlFor="client_billing_country" className="font-body">Country</Label>
                <Select 
                  value={formData.client_billing_country} 
                  onValueChange={(value) => setFormData({ ...formData, client_billing_country: value })}
                >
                  <SelectTrigger className="font-body">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="United Kingdom">United Kingdom</SelectItem>
                    <SelectItem value="Ireland">Ireland</SelectItem>
                    <SelectItem value="United States">United States</SelectItem>
                    <SelectItem value="Canada">Canada</SelectItem>
                    <SelectItem value="Australia">Australia</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          {/* Invoice Project Section - Paify Plus Feature */}
          <div className={`border border-gray-200 rounded-lg p-4 ${!hasPlusSubscription ? 'opacity-60' : ''}`}>
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="flex items-center space-x-2">
                  <h3 className="text-lg font-semibold text-gray-900 font-heading">Invoice Project</h3>
                  <span className="inline-block px-2 py-1 text-xs font-medium bg-purple-100 text-purple-800 rounded">Paify Plus</span>
                </div>
                <p className="text-sm text-gray-600 font-body">Create as invoice project with structured payment through Paify</p>
              </div>
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  if (!hasPlusSubscription) return;
                  console.log('Toggle clicked, current state:', formData.is_invoice_project);
                  setFormData(prev => ({
                    ...prev, 
                    is_invoice_project: !prev.is_invoice_project
                  }));
                }}
                disabled={!hasPlusSubscription}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  formData.is_invoice_project && hasPlusSubscription ? 'bg-[#e6007e]' : 'bg-gray-200'
                } ${!hasPlusSubscription ? 'cursor-not-allowed' : ''}`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    formData.is_invoice_project && hasPlusSubscription ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>
            
            {!hasPlusSubscription ? (
              <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                <p className="text-sm text-purple-800 font-body">
                  This feature is available with Paify Plus or Embedded subscription. <a href="/subscriptions" className="text-[#e6007e] hover:underline font-medium">Upgrade now</a>
                </p>
              </div>
            ) : formData.is_invoice_project && (
              <div className="space-y-6">
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <p className="text-sm text-blue-800 font-body">
                    <strong>Invoice Mode:</strong> Auto-generates a structured invoice from this project. Your client pays you directly via your connected Stripe account or your bank details — Paify never holds or processes funds.
                  </p>
                </div>
                
                {/* Invoice Type Selection */}
                <div>
                  <Label className="font-body text-base mb-3 block">Invoice Management</Label>
                  <div className="space-y-3">
                    {/* Option 1: Generate in Paify */}
                    <label className="flex items-start space-x-3 p-4 border rounded-lg cursor-pointer hover:bg-gray-50">
                      <input
                        type="radio"
                        name="invoice_type"
                        value="generated"
                        checked={formData.invoice_type === 'generated'}
                        onChange={(e) => setFormData({...formData, invoice_type: e.target.value})}
                        className="mt-1"
                      />
                      <div className="flex-1">
                        <div className="flex items-center space-x-2 mb-2">
                          <FileText className="w-4 h-4 text-blue-600" />
                          <span className="font-semibold text-gray-900 font-heading">Create Invoice in Paify</span>
                        </div>
                        <p className="text-sm text-gray-600 font-body">
                          Auto-generate professional invoice from project details
                        </p>
                        {formData.invoice_type === 'generated' && (
                          <div className="mt-3 grid grid-cols-2 gap-3">
                            <div>
                              <Label className="text-xs font-body">Payment Due (Days)</Label>
                              <Input
                                type="number"
                                min="1"
                                max="90"
                                value={formData.invoice_due_days}
                                onChange={(e) => setFormData({ ...formData, invoice_due_days: parseInt(e.target.value) })}
                                className="font-body text-sm"
                              />
                            </div>
                            <div>
                              <Label className="text-xs font-body">Reference</Label>
                              <Input
                                value={formData.external_invoice_reference}
                                onChange={(e) => setFormData({ ...formData, external_invoice_reference: e.target.value })}
                                placeholder="PO-2024-001"
                                className="font-body text-sm"
                              />
                            </div>
                          </div>
                        )}
                      </div>
                    </label>

                    {/* Option 2: Upload Client's Invoice */}
                    <label className="flex items-start space-x-3 p-4 border rounded-lg cursor-pointer hover:bg-gray-50">
                      <input
                        type="radio"
                        name="invoice_type"
                        value="uploaded"
                        checked={formData.invoice_type === 'uploaded'}
                        onChange={(e) => setFormData({...formData, invoice_type: e.target.value})}
                        className="mt-1"
                      />
                      <div className="flex-1">
                        <div className="flex items-center space-x-2 mb-2">
                          <Upload className="w-4 h-4 text-green-600" />
                          <span className="font-semibold text-gray-900 font-heading">Attach Client's Invoice</span>
                        </div>
                        <p className="text-sm text-gray-600 font-body">
                          Upload invoice file created independently by client
                        </p>
                        {formData.invoice_type === 'uploaded' && (
                          <div className="mt-3">
                            <Label className="text-xs font-body">Upload Invoice File</Label>
                            <input
                              type="file"
                              accept=".pdf,.jpg,.jpeg,.png"
                              onChange={(e) => {
                                const file = e.target.files[0];
                                if (file) {
                                  setUploadedFile(file);
                                  setFormData({...formData, external_invoice_reference: file.name});
                                }
                              }}
                              className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                            />
                            {uploadedFile && (
                              <p className="text-xs text-green-600 mt-1 font-body">
                                ✓ {uploadedFile.name} ready to upload
                              </p>
                            )}
                          </div>
                        )}
                      </div>
                    </label>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* VAT Settings - Paify Plus Feature */}
          <div className={`border rounded-lg p-4 bg-gradient-to-r from-green-50 to-blue-50 ${!hasPlusSubscription ? 'opacity-60' : ''}`}>
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center space-x-2">
                <PiggyBank className="w-4 h-4 text-green-600" />
                <Label className="font-semibold text-gray-900 font-heading">VAT Settings</Label>
                <span className="inline-block px-2 py-1 text-xs font-medium bg-purple-100 text-purple-800 rounded">Paify Plus</span>
              </div>
              {hasPlusSubscription ? (
                <div className="flex items-center space-x-2">
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.vat_enabled}
                      onChange={(e) => {
                        console.log('VAT toggle clicked:', e.target.checked);
                        setFormData({ 
                          ...formData, 
                          vat_enabled: e.target.checked 
                        });
                        console.log('Updated formData.vat_enabled to:', e.target.checked);
                      }}
                      className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-green-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-green-500"></div>
                  </label>
                  <span className="text-sm font-body">
                    {formData.vat_enabled ? 'Enabled' : 'Disabled'}
                  </span>
                </div>
              ) : (
                <div className="flex items-center space-x-2">
                  <div className="w-11 h-6 bg-gray-200 rounded-full cursor-not-allowed"></div>
                  <span className="text-sm font-body text-gray-400">Disabled</span>
                </div>
              )}
            </div>
            {!hasPlusSubscription ? (
              <div className="bg-purple-50 border border-purple-200 rounded-lg p-3 mt-2">
                <p className="text-sm text-purple-800 font-body">
                  This feature is available with Paify Plus or Embedded subscription. <a href="/subscriptions" className="text-[#e6007e] hover:underline font-medium">Upgrade now</a>
                </p>
              </div>
            ) : (
              <>
                {formData.vat_enabled && (
                  <div className="mt-3">
                    <Label className="text-xs font-body">VAT Rate (%)</Label>
                    <Input
                      type="number"
                      value={formData.vat_rate}
                      onChange={(e) => setFormData({ ...formData, vat_rate: parseFloat(e.target.value) || 0 })}
                      placeholder="20"
                      className="font-body text-sm w-24"
                      min="0"
                      max="100"
                      step="0.1"
                    />
                  </div>
                )}
                <div className="text-xs text-gray-600 font-body mt-2">
                  {formData.vat_enabled ? (
                    <p>✓ VAT will be calculated at {formData.vat_rate}% and included in invoices</p>
                  ) : (
                    <p>⚪ No VAT will be applied to this project</p>
                  )}
                </div>
              </>
            )}
          </div>

          {/* Save as Template — opt-in, user-controlled */}
          <div data-testid="save-as-template-panel" className="bg-white border border-gray-200 rounded-lg p-4">
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                data-testid="save-as-template-checkbox"
                checked={saveAsTemplate}
                onChange={(e) => setSaveAsTemplate(e.target.checked)}
                className="mt-1 w-4 h-4 accent-[#e6007e] cursor-pointer"
              />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <Bookmark className="w-4 h-4 text-[#e6007e]" />
                  <span className="font-heading text-sm font-semibold text-gray-900">Save this as a template</span>
                </div>
                <p className="text-xs text-gray-500 font-body mt-0.5">
                  Reuse these details (client, scope, amount, VAT) for future projects with one click.
                </p>
              </div>
            </label>
            {saveAsTemplate && (
              <div className="mt-3 ml-7">
                <Label htmlFor="template_name_input" className="text-xs font-body text-gray-700">Template name</Label>
                <Input
                  id="template_name_input"
                  name="template_name_no_autofill"
                  data-testid="template-name-input"
                  value={templateName}
                  onChange={(e) => setTemplateName(e.target.value)}
                  placeholder='e.g. "Monthly retainer — Acme Co."'
                  className="font-body mt-1 max-w-md"
                  autoComplete="off"
                  autoCorrect="off"
                  spellCheck="false"
                  data-form-type="other"
                  data-lpignore="true"
                  data-1p-ignore="true"
                  maxLength={80}
                />
                <p className="text-xs text-gray-400 font-body mt-1">
                  Saves after the project is created. Required to save the template.
                </p>
              </div>
            )}
          </div>

          <div className="flex flex-col sm:flex-row items-center justify-between gap-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              className="font-body w-full sm:w-auto"
            >
              Cancel
            </Button>
            <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
              <Button
                type="button"
                disabled={loading}
                variant="outline"
                className="font-body w-full sm:w-auto border-gray-300"
                onClick={(e) => handleSubmit(e, false)}
              >
                {loading && !sendingToClient ? "Creating..." : "Create Project"}
              </Button>
              <Button
                type="button"
                disabled={loading}
                className="bg-black hover:bg-gray-800 text-white font-body w-full sm:w-auto"
                onClick={(e) => {
                  e.preventDefault();
                  setShowSendConfirmation(true);
                }}
                title="Create and email client immediately"
              >
                <Send className="w-4 h-4 mr-2" />
                Create & Send to Client
              </Button>
            </div>
          </div>
          
        </form>
      </DialogContent>
    </Dialog>
    
    {/* Send Confirmation Dialog - Using AlertDialog for proper layering */}
    <AlertDialog open={showSendConfirmation} onOpenChange={setShowSendConfirmation}>
      <AlertDialogContent className="max-w-md">
        <AlertDialogHeader>
          <AlertDialogTitle className="font-heading">Send to Client?</AlertDialogTitle>
          <AlertDialogDescription asChild>
            <div className="space-y-4">
              <p className="text-gray-600 font-body">
                This will immediately email <strong className="text-gray-900">{formData.client_name || 'the client'}</strong> at <strong className="text-gray-900">{formData.client_email || 'their email'}</strong> with a link to view payment options and sign the contract.
              </p>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                <p className="text-sm text-blue-800 font-body">
                  <strong>Project:</strong> {formData.title || 'Untitled'}<br/>
                  <strong>Amount:</strong> £{parseFloat(formData.total_amount || 0).toLocaleString()}
                  {formData.vat_enabled && ` + VAT (${formData.vat_rate}%)`}
                </p>
              </div>
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel className="font-body">Cancel</AlertDialogCancel>
          <AlertDialogAction 
            onClick={(e) => handleSubmit(e, true)}
            disabled={sendingToClient}
            className="bg-black hover:bg-gray-800 text-white font-body"
          >
            {sendingToClient ? "Sending..." : "Yes, Send Now"}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  </>
  );
};

// Project Detail Component

export default CreateProjectModal;
